import Cookies from 'js-cookie';
import { refreshTokenPromise, logoutAndRedirect, showErrorMessage } from './axios';

/**
 * SSE 流式请求（基于 fetch + ReadableStream）
 * 用于 AI 对话等需要逐字返回的场景
 *
 * 后端接口：POST /chat/chat
 * - 参数通过 query 传递：content（用户问题，必填）、sessionId（会话 ID，可选）
 * - 返回 SSE 流，格式为：
 *   data: [PROGRESS]正在分析您的问题  → 处理进度
 *   data: 您                          → 正文 token（逐字推送）
 *   data: [DONE]:356407375118467072,360820341888520192  → 流结束，冒号后逗号分隔：会话 ID,消息 ID
 *
 * token 处理与 axios 保持一致：
 * - 业务 code 402 → 换新 token 后重放本次请求（只重试一次）
 * - 业务 code 401 / 405 → 清理登录态并跳转登录页
 *
 * @param {string} url - 接口路径（相对路径会自动拼 /api/）
 * @param {object} params - query 参数集合
 * @param {object} callbacks - 回调函数集合
 * @param {function} callbacks.onChunk - 每次收到数据块的回调 (data) => void
 * @param {function} callbacks.onDone - 流结束回调 () => void
 * @param {function} callbacks.onError - 错误回调 (error) => void
 * @returns {object} - { abort: () => void } 用于中断请求
 *
 * @example
 * const request = sseRequest('/portal/chat/chat', { content: '你好' }, {
 *   onChunk: (data) => console.log(data.content),
 *   onDone: () => console.log('完成'),
 *   onError: (err) => console.error(err),
 * });
 * // 需要中断时调用
 * request.abort();
 */
export default function sseRequest(url, params, callbacks = {}) {
  const { onChunk, onDone, onError } = callbacks;
  let controller = new AbortController();
  let reader = null;
  let requestAborted = false;
  let streamFinished = false;
  let doneReceived = false;
  let errorReported = false;
  // 是否已经换新 token 后重放过一次，避免 token 一直过期时死循环
  let retried = false;

  function reportError(error) {
    if (requestAborted || errorReported || streamFinished) {
      return;
    }
    errorReported = true;
    if (onError) {
      onError(error);
    }
  }

  // 拼接完整 URL（复用项目的 devServer proxy），参数通过 query 传递
  const query = Object.keys(params || {})
    .filter(key => params[key] !== undefined && params[key] !== null && params[key] !== '')
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');
  const baseUrl = url.startsWith('http') ? url : `/api${url}`;
  const fullUrl = query ? `${baseUrl}?${query}` : baseUrl;

  // 后端在 token 过期等异常场景会返回普通 JSON，而不是 SSE 流
  function isJsonResponse(response) {
    const contentType = response.headers.get('Content-Type') || '';
    return contentType.indexOf('application/json') !== -1;
  }

  // 读取 JSON 响应体，解析失败返回 null
  function readJson(response) {
    return response.text().then((text) => {
      try {
        return JSON.parse(text);
      } catch (e) {
        return null;
      }
    }).catch(() => null);
  }

  /**
   * 按 axios 响应拦截器的规则处理业务状态码
   * @param {object} data - 后端返回的 JSON
   * @returns {boolean} - true 表示已处理，本次请求不再往下走流式解析
   */
  function handleBizError(data) {
    const code = data && data.code;
    // 0 成功、408 微信登录跳转绑定，都放行
    if (!code || code === 0 || code === 408) {
      return false;
    }
    // 402 与 axios 保持一致：换新 token 后重放请求
    if (code === 402) {
      refreshAndRetry();
      return true;
    }
    // 401 token 错误、405 token 已经完全过期
    if (code === 401 || code === 405) {
      logoutAndRedirect();
      reportError(new Error((data && data.msg) || '登录状态已失效'));
      return true;
    }
    showErrorMessage(data && data.msg);
    reportError(new Error((data && data.msg) || '服务异常'));
    return true;
  }

  // 参照 axios：token 过期先换新 token，再用新 token 重放本次 SSE 请求
  function refreshAndRetry() {
    if (requestAborted || streamFinished || retried) {
      reportError(new Error('登录状态已失效'));
      return;
    }
    retried = true;
    refreshTokenPromise().then(() => {
      // 换新 token 后重新发起请求，此时还没有正文输出，不会产生重复内容
      controller = new AbortController();
      streamFinished = false;
      request();
    });
  }

  async function request() {
    // 发起前主动预刷新 token：对齐门户 axios 请求拦截器的「主动刷新」模型。
    // sseRequest 走原生 fetch，绕过了 axios 拦截器，拿不到拦截器里的预刷新，
    // 因此这里手动判断 tokenTimeOut（unix 秒）是否已过期，过期就先换新 token 再发起，
    // 避免带着过期 token 发起 SSE 请求。（另保留下方 402 被动兜底，双保险）
    const now = Math.floor(Date.now() / 1000);
    const tokenTimeOut = Number(Cookies.get('tokenTimeOut')) || 0;
    if (Cookies.get('token') && tokenTimeOut && now >= tokenTimeOut) {
      await refreshTokenPromise();
    }
    fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        // 按标准 SSE 请求声明接收类型，避免网关/后端按普通 JSON 请求处理
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        'clientToken': 'Basic YW5kcm9pZDphbmRyb2lk',
        'Authorization': Cookies.get('token') || '',
        'x-language-header': Cookies.get('langCode') || 'CN',
        'hostIp': localStorage.hostIp || '192.168.11.127',
      },
      signal: controller.signal,
    })
      .then(async (response) => {
        // token 过期等场景下返回的是普通 JSON，需要先按业务状态码处理
        if (!response.ok || isJsonResponse(response)) {
          const data = await readJson(response);
          // HTTP 401 / 405 直接判定为登录失效
          if (response.status === 401 || response.status === 405) {
            logoutAndRedirect();
            reportError(new Error((data && data.msg) || '登录状态已失效'));
            return;
          }
          // 其余状态码先按业务 code 处理：402 会自动换新 token 后重放
          if (handleBizError(data)) {
            return;
          }
          // 处理 HTTP 错误
          if (!response.ok) {
            const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
            error.status = response.status;
            reportError(error);
            return;
          }
        }

        if (!response.body || !response.body.getReader) {
          throw new Error('当前浏览器不支持流式响应');
        }

        reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = ''; // 缓冲区，处理跨块分割的数据

        function finishStream() {
          // [DONE] 和 EOF 都可能触发完成，统一从这里保证只回调一次。
          if (streamFinished || requestAborted) {
            return;
          }
          streamFinished = true;
          if (onDone) {
            onDone();
          }
        }

        // 收到数据块后立即回调，不做任何延迟处理
        function emitChunk(chunk) {
          if (requestAborted || !onChunk) {
            return;
          }
          try {
            onChunk(chunk);
          } catch (error) {
            reportError(error);
            requestAborted = true;
            controller.abort();
          }
        }

        // 递归读取流
        function readStream() {
          reader.read().then(({ done, value }) => {
            if (done) {
              // 流结束时刷新 TextDecoder，避免最后一个 UTF-8 字符留在解码器里。
              buffer += decoder.decode();
              processBuffer(true);
              // 没有收到 [DONE] 时，用 EOF 作为结束信号
              if (!doneReceived) {
                finishStream();
              }
              return;
            }

            // 解码并拼接到缓冲区
            buffer += decoder.decode(value, { stream: true });
            processBuffer();
            readStream(); // 继续读取
          }).catch((err) => {
            // 用户主动 abort 不算错误
            if (err.name !== 'AbortError') {
              reportError(err);
            }
          });
        }

        // 处理缓冲区内容
        // 接口返回纯文本 SSE 行，兼容以下几种：
        // 1. data: [PROGRESS]正在分析您的问题  → 处理进度
        // 2. data: 您                          → 正文 token（逐字推送）
        // 3. data:                             → Markdown 换行
        // 4. data: [DONE]:356407375118467072,360820341888520192  → 流结束，冒号后为会话 ID,消息 ID
        // 5. data: {"type":"delta","content":"..."} → JSON 格式（保留兼容）
        function processBuffer(flush) {
          // SSE 可能使用 LF、CRLF 或 CR，统一按行处理。
          const lines = buffer.split(/\r\n|\n|\r/);

          // 保留最后一个不完整的行等待后续数据补全；flush 时直接处理剩余内容。
          buffer = flush ? '' : (lines.pop() || '');

          lines.forEach((line) => {
            // 只去除行首空格，保留正文 token 里的空格。
            const normalizedLine = line.replace(/^\s+/, '');
            if (!normalizedLine) {
              return;
            }
            // SSE 数据行
            if (normalizedLine.startsWith('data:')) {
              // 后端把 token 原样放在 data: 后面，没有规范里的分隔空格：
              // 空的 data: 表示换行，"data: "（只有一个空格）也是正文内容，
              // 不能吞掉 data: 后的空格，否则 Markdown 表格、加粗等语法会被破坏。
              handleData(normalizedLine.slice(5));
              return;
            }
            // 兼容不带 data: 前缀的过程状态事件
            if (normalizedLine.startsWith('[PROGRESS]') || normalizedLine.startsWith('[DONE]')) {
              handleData(normalizedLine);
            }
          });
        }

        // 解析单条数据内容并分发到对应回调
        function handleData(content) {
          // 后端用空的 data: 表示 Markdown 换行，不能丢弃，否则表格、列表和段落会被拼成一行。
          if (content === '') {
            emitChunk({ type: 'delta', content: '\n' });
            return;
          }
          const normalizedContent = content.trim();
          // 过程状态事件，兼容两种写法：[PROGRESS]xxx 与 [PROGRESS]:xxx
          if (normalizedContent.startsWith('[PROGRESS]')) {
            const text = normalizedContent.slice(10).replace(/^:/, '').trim();
            if (text) {
              emitChunk({ type: 'progress', content: text });
            }
            return;
          }
          // 结束事件，如：[DONE]:356407375118467072,360820341888520192
          // 冒号后逗号分隔，依次为会话 ID、本次回答的消息 ID（用于点赞/点踩反馈）
          if (normalizedContent.startsWith('[DONE]')) {
            const doneIds = normalizedContent.slice(6).replace(/^:/, '').trim().split(',');
            const sessionId = (doneIds[0] || '').trim();
            const msgId = (doneIds[1] || '').trim();
            doneReceived = true;
            emitChunk({ type: 'done', sessionId: sessionId || null, msgId: msgId || null });
            finishStream();
            return;
          }
          // JSON 格式数据块（保留兼容）
          if (normalizedContent.startsWith('{')) {
            try {
              const data = JSON.parse(normalizedContent);
              // 流过程中也可能推送业务异常（如 token 过期），处理规则与 axios 一致
              if (handleBizError(data)) {
                return;
              }
              if (data && data.type === 'done') {
                doneReceived = true;
              }
              emitChunk(data);
              if (doneReceived) {
                finishStream();
              }
              return;
            } catch (e) {
              console.error('[SSE Parse Error]', e, content);
            }
          }
          // 普通正文 token，逐字拼接
          emitChunk({ type: 'delta', content });
        }

        readStream();
      })
      .catch((err) => {
        // fetch 阶段的错误（网络错误、URL 错误等）
        if (err.name !== 'AbortError') {
          reportError(err);
        }
      });
  }

  request();

  return {
    abort: () => {
      requestAborted = true;
      controller.abort();
      if (reader) {
        reader.cancel().catch(() => {});
      }
    },
  };
}
